import React from 'react';
import { fmtMoney, fmtNum, fmtDate } from '../lib/utils';
import PrintHeader, { DEFAULT_COMPANY } from './PrintHeader';

const BLUE = '#1E3A8A';

const MONTH_LABEL = (m) => (m ? new Date(`${m}-01`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '—');

export default function SalaryStatementPrint({ st, company, unitLabel }) {
  const c = { ...DEFAULT_COMPANY, ...(company || {}) };
  const rows = st?.months || [];
  const t = st?.totals || {};
  return (
    <div className="bg-white text-slate-900 font-sans p-4" style={{ minWidth: 680 }}>
      <PrintHeader company={c} />

      <div className="flex items-center justify-between my-5" style={{ color: BLUE }}>
        <div className="text-sm w-44">
          <p className="font-semibold">Statement Date</p>
          <p className="num text-[15px] mt-1" dir="ltr">{fmtDate(new Date().toISOString().slice(0, 10))}</p>
        </div>
        <div className="px-8 py-2 rounded-full border-2 text-center whitespace-nowrap" style={{ borderColor: BLUE }}>
          <span className="font-arabic text-2xl leading-none align-middle" dir="rtl" style={{ unicodeBidi: 'isolate' }}>كشف الرواتب</span>
          <span className="mx-3 opacity-40">·</span>
          <span className="font-heading font-bold text-base uppercase tracking-wide align-middle">Salary Statement</span>
        </div>
        <div className="text-sm w-44 text-right">
          <p className="font-semibold">Outstanding Advance</p>
          <p className="num text-[15px] mt-1" dir="ltr">{fmtMoney(st?.advanceOutstanding, c.currency)}</p>
        </div>
      </div>

      <div className="mb-5 text-[15px]" style={{ color: BLUE }}>
        <span className="font-semibold text-sm">Employee / <span className="font-arabic" style={{ unicodeBidi: 'isolate' }}>الموظف</span>:</span>{' '}
        <span className="font-bold text-slate-900 border-b border-dotted border-slate-400 px-2 text-[18px]">{st?.name}</span>
        <span className="ml-4 text-sm capitalize">({st?.meta?.role || st?.kind})</span>
      </div>

      <table className="w-full mb-6 border" style={{ borderColor: BLUE }}>
        <thead>
          <tr className="text-white text-left" style={{ backgroundColor: BLUE }}>
            <th className="px-3 py-2.5 text-sm font-semibold w-28">Month</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right">{unitLabel}</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right">Earned / Salary</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right">Advance Given</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right">Advance Deducted</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right">Paid</th>
            <th className="px-3 py-2.5 text-sm font-semibold text-right">Net Cash</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.month} className={i % 2 ? 'bg-[#FDF4F6]' : ''} style={{ borderTop: '1px solid #dbe3f5' }} data-testid="print-statement-row">
              <td className="px-3 py-2.5 text-sm num whitespace-nowrap">{MONTH_LABEL(r.month)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px]">{fmtNum(r.units)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(r.earned, c.currency)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(r.advanceGiven, c.currency)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(r.advanceDeducted, c.currency)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(r.paid, c.currency)}</td>
              <td className="px-3 py-2.5 text-right num text-[15px] font-semibold whitespace-nowrap">{fmtMoney(r.netPaid, c.currency)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-bold" style={{ borderColor: BLUE }}>
            <td className="px-3 py-2.5 text-[15px]">Totals</td>
            <td className="px-3 py-2.5 text-right num text-[15px]">{fmtNum(t.units)}</td>
            <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(t.earned, c.currency)}</td>
            <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(t.advanceGiven, c.currency)}</td>
            <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(t.advanceDeducted, c.currency)}</td>
            <td className="px-3 py-2.5 text-right num text-[15px]">{fmtMoney(t.paid, c.currency)}</td>
            <td className="px-3 py-2.5 text-right num text-[16px] whitespace-nowrap">{fmtMoney(t.netPaid, c.currency)}</td>
          </tr>
        </tbody>
      </table>
      {!rows.length && <p className="text-center text-slate-400 text-sm mb-6">No salary activity recorded.</p>}

      {st?.details?.length > 0 && (
        <>
          <p className="text-sm font-bold mb-2" style={{ color: BLUE }}>Details · <span className="font-arabic">التفاصيل</span></p>
          <table className="w-full mb-6 border" style={{ borderColor: '#dbe3f5' }}>
            <thead>
              <tr className="text-left bg-slate-100">
                <th className="px-3 py-2 text-xs font-semibold w-28">Date</th>
                <th className="px-3 py-2 text-xs font-semibold w-28">Type</th>
                <th className="px-3 py-2 text-xs font-semibold">Description</th>
                <th className="px-3 py-2 text-xs font-semibold text-right w-24">Qty</th>
                <th className="px-3 py-2 text-xs font-semibold text-right w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {st.details.map((d, i) => (
                <tr key={i} style={{ borderTop: '1px solid #eef2f9' }}>
                  <td className="px-3 py-1.5 text-xs num whitespace-nowrap">{fmtDate(d.date)}</td>
                  <td className="px-3 py-1.5 text-xs">{d.type}</td>
                  <td className="px-3 py-1.5 text-xs">{d.description}</td>
                  <td className="px-3 py-1.5 text-xs text-right num">{d.units ? fmtNum(d.units) : '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-right num">{fmtMoney(d.earned ?? d.amount ?? d.advanceGiven ?? 0, c.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="flex justify-between items-end mt-10 text-sm" style={{ color: BLUE }}>
        <div className="text-center">
          <div className="border-t border-dotted border-slate-400 w-56 mb-1.5" />
          <p>Employee's Signature · <span className="font-arabic text-base">توقيع الموظف</span></p>
        </div>
        <div className="text-center">
          <div className="border-t border-dotted border-slate-400 w-56 mb-1.5" />
          <p>For {c.name} · <span className="font-arabic text-base">التوقيع</span></p>
        </div>
      </div>
    </div>
  );
}
